import { TagColorEnum } from "@/__generated__/graphql";
const TOPBAR_HEIGHT = 48;
const SUBNAV_HEIGHT = 48;
const PUBLIC_TOPBAR_HEIGHT = 64;
const SIDEBAR_WIDTH = SUBNAV_HEIGHT * 5;
const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT || "local";
const IS_LOCAL_ENVIRONMENT = ENVIRONMENT == "local";
const IS_DEV_ENVIRONMENT = ENVIRONMENT == "development";
const DEFAULT_HEADER_HEIGHT = 46.5;
const DEFAULT_ROW_HEIGHT = 44;
const COMPACT_ROW_HEIGHT = DEFAULT_ROW_HEIGHT * 0.72;
const DEFAULT_COLUMN_WIDTH = 150;

const Values = {
  APP_VIEW_ENTITY_MODE_MAP: {
    thread: "multiple",
    "financial-accounts": "multiple",
    "chart-of-accounts": "single",
    contacts: "multiple",
    organizations: "multiple",
    documents: "multiple",
    "journal-entries": "multiple",
    "tax-codes": "multiple",
    triage: "multiple",
    // A budget is one business's, or a person's own.
    budget: "single",
    reports: "multiple",
    transactions: "multiple",
    rules: "multiple",
  },
  APP_VIEW_TAB_MODE_MAP: {
    thread: {
      type: "multiple" as const,
      values: [],
    },
    "financial-accounts": {
      type: "multiple" as const,
      values: ["all", "debit", "credit"],
    },
    "chart-of-accounts": {
      type: "multiple" as const,
      values: [],
    },
    contacts: {
      type: "multiple" as const,
      values: ["all", "employees", "suppliers", "customers"],
    },
    organizations: {
      type: "multiple" as const,
      values: [],
    },
    documents: {
      type: "multiple" as const,
      values: ["all", "invoices", "unpaid-invoices", "bills", "unreconciled"],
    },

    "journal-entries": {
      type: "multiple" as const,
      values: [],
    },
    "tax-codes": {
      type: "multiple" as const,
      values: [],
    },
    triage: {
      type: "multiple" as const,
      values: [],
    },
    budget: {
      type: "multiple" as const,
      values: [],
    },
    reports: {
      type: "multiple" as const,
      values: [],
    },
    transactions: {
      type: "multiple" as const,
      values: ["bank-feed", "posted", "unposted"],
    },
    rules: {
      type: "multiple" as const,
      values: [],
    },
  },
  COMPANY_NAME: "NeoLedger",
  DEFAULT_COLUMN_WIDTH: DEFAULT_COLUMN_WIDTH,
  DEFAULT_COLUMN_WIDTH_PX: DEFAULT_COLUMN_WIDTH + "px",
  DEFAULT_HEADER_HEIGHT: DEFAULT_HEADER_HEIGHT,
  DEFAULT_HEADER_HEIGHT_PX: DEFAULT_HEADER_HEIGHT + "px",
  DEFAULT_PADDING_X: 4,
  DEFAULT_PADDING_Y: 4,
  DEFAULT_ROW_HEIGHT: DEFAULT_ROW_HEIGHT,
  DEFAULT_ROW_HEIGHT_PX: DEFAULT_ROW_HEIGHT + "px",
  COMPACT_ROW_HEIGHT: COMPACT_ROW_HEIGHT,
  COMPACT_ROW_HEIGHT_PX: COMPACT_ROW_HEIGHT + "px",
  DOMAIN: {
    local: "http://localhost:3000",
    development: "https://dev.neoledger.finance",
    production: "https://neoledger.finance",
  }[ENVIRONMENT],
  ENVIRONMENT: import.meta.env.VITE_ENVIRONMENT || "local",
  // The budget is still being built: shown on a local stack alone, till it is ready.
  IS_BUDGET_SHOWN: IS_LOCAL_ENVIRONMENT,
  IS_DEV_ENVIRONMENT: ENVIRONMENT == "development",
  IS_LOCAL_DEV_ENVIRONMENT: IS_LOCAL_ENVIRONMENT || IS_DEV_ENVIRONMENT,
  IS_LOCAL_ENVIRONMENT: ENVIRONMENT == "local",
  MINIMIZED_SIDEBAR_WIDTH: 50,
  MINIMIZED_SIDEBAR_WIDTH_PX: 50 + "px",
  PUBLIC_TOPBAR_HEIGHT: PUBLIC_TOPBAR_HEIGHT,
  PUBLIC_TOPBAR_HEIGHT_PX: PUBLIC_TOPBAR_HEIGHT + "px",
  ROW_TYPE_VALUES: {
    BRANCH: "BRANCH",
    LEAF: "LEAF",
  },
  SIDEBAR_WIDTH: SIDEBAR_WIDTH,
  SIDEBAR_WIDTH_PX: SIDEBAR_WIDTH + "px",
  SUBNAV_HEIGHT: SUBNAV_HEIGHT,
  SUBNAV_HEIGHT_PX: SUBNAV_HEIGHT + "px",
  TOKENS: {
    local: {
      STRIPE: {
        PUBLIC_KEY:
          "pk_test_51IYbQDL98fJoS6kxbtqDBoJf87u21kdAVhnlsXvYfl5DrxJrxycrgQo0eB6cpOCEGAWV74mOzT76jRmfQIgrWbWl007yLVZpar",
        CLIENT_ID:
          import.meta.env.VITE_STRIPE_CLIENT_ID || "ca_test_placeholder",
      },
    },
    development: {
      STRIPE: {
        PUBLIC_KEY:
          "pk_test_51IYbQDL98fJoS6kxbtqDBoJf87u21kdAVhnlsXvYfl5DrxJrxycrgQo0eB6cpOCEGAWV74mOzT76jRmfQIgrWbWl007yLVZpar",
        CLIENT_ID:
          import.meta.env.VITE_STRIPE_CLIENT_ID || "ca_test_placeholder",
      },
    },
    production: {
      STRIPE: {
        PUBLIC_KEY:
          "pk_test_51IYbQDL98fJoS6kxbtqDBoJf87u21kdAVhnlsXvYfl5DrxJrxycrgQo0eB6cpOCEGAWV74mOzT76jRmfQIgrWbWl007yLVZpar",
        CLIENT_ID:
          import.meta.env.VITE_STRIPE_CLIENT_ID || "ca_live_placeholder",
      },
    },
  }[ENVIRONMENT],
  TOPBAR_HEIGHT: TOPBAR_HEIGHT,
  TOPBAR_HEIGHT_PX: TOPBAR_HEIGHT + "px",
  URLS: {
    local: {
      EMAIL: "neoledger.finance",
      WEBAPP: "http://127.0.0.1:3111",
      // ``VITE_LOCAL_API_ORIGIN`` points a second dev server at a second
      // backend (the marketing capture stack runs one on :8002).
      REST: `http://${import.meta.env.VITE_LOCAL_API_ORIGIN || "127.0.0.1:8001"}`,
      GRAPHQL: `http://${import.meta.env.VITE_LOCAL_API_ORIGIN || "127.0.0.1:8001"}/graphql/`,
      GRAPHQL_WS: `ws://${import.meta.env.VITE_LOCAL_API_ORIGIN || "127.0.0.1:8001"}/graphql/`,
    },
    development: {
      EMAIL: "neoledger.finance",
      WEBAPP: "https://dev.neoledger.finance/",
      REST: "https://api.dev.neoledger.finance",
      GRAPHQL: "https://api.dev.neoledger.finance/graphql/",
      GRAPHQL_WS: "wss://api.dev.neoledger.finance/graphql/",
    },
    production: {
      EMAIL: "neoledger.finance",
      WEBAPP: "https://neoledger.finance/",
      REST: "https://api.neoledger.finance",
      GRAPHQL: "https://api.neoledger.finance/graphql/",
      GRAPHQL_WS: "wss://api.neoledger.finance/graphql/",
    },
  }[ENVIRONMENT],
  // A tab's label when its key does not read as one.
  APP_VIEW_TAB_LABELS: {
    "unpaid-invoices": "Invoices (Unpaid)",
  } as Record<string, string>,
  VIEW_ALIAS_MAP: {
    invoices: {
      view: "documents",
      tab: "invoices",
    },
    bills: {
      view: "documents",
      tab: "bills",
    },
    unreconciled: {
      view: "documents",
      tab: "unreconciled",
    },
  },
};

/** The charts' colours, in the order a chart's series take them. */
/**
 * What the landing page's feature illustrations are drawn in: the divider's
 * grey taken towards the tertiary words' in steps, and that colour itself.
 */
export const ILLUSTRATION_COLORS = {
  color1: `color-mix(in srgb, ${"var(--border)"} 80%, ${"color-mix(in oklab, var(--muted-foreground) 80%, transparent)"} 20%)`,
  color2: `color-mix(in srgb, ${"var(--border)"} 50%, ${"color-mix(in oklab, var(--muted-foreground) 80%, transparent)"} 50%)`,
  color3: `color-mix(in srgb, ${"var(--border)"} 20%, ${"color-mix(in oklab, var(--muted-foreground) 80%, transparent)"} 80%)`,
  color4: "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
} as const;

export const CHART_COLORS = [
  "#302FE9",
  "#E08300",
  "#E3B920",
  "#A65430",
  "#BB9463",
  "#1645AC",
  "#B0C6D4",
  "#32443b",
  "#7cac94",
];

/**
 * The colours a tag, a state or a kind is drawn in. In each scheme every
 * colour's words, and its ground, are as light as the grey's - the same
 * relative luminance - so greyscaled they come out alike and each reads with
 * the grey's contrast: only the hue tells them apart
 * (``tests/src/unit/enumColorsGreyAlike.test.ts``). GREY_2 is the grey drawn
 * the other way round.
 */
export const EnumColors = {
  GREY_1: {
    solidColor: "#222222",
    dark: {
      color: "#c7c7c7",
      backgroundColor: "#24262b",
      borderColor: "#27292f",
    },
    light: {
      color: "#424242",
      backgroundColor: "#f0f0f0",
      borderColor: "#e4e4e4",
    },
  },
  GREY_2: {
    solidColor: "#e4e4e4",
    light: {
      color: "#f6f6f6",
      backgroundColor: "#222222",
      borderColor: "#323232",
    },
    dark: {
      color: "#424242",
      backgroundColor: "#f6f6f6",
      borderColor: "#e4e4e4",
    },
  },
  RED: {
    solidColor: "#ff3535",
    dark: {
      color: "#ffb4b2",
      backgroundColor: "#2e2424",
      borderColor: "#4D3030",
    },
    light: {
      color: "#7d1f1b",
      backgroundColor: "#ffebee",
      borderColor: "#ffd3d8",
    },
  },
  ORANGE: {
    // solidColor: "#ff9600",
    solidColor: CHART_COLORS[1],
    dark: {
      color: "#f2be76",
      backgroundColor: "#352308",
      // borderColor: "#46342c",
      borderColor: `color-mix(in srgb, ${CHART_COLORS[1]} 45%, #1b1d21)`,
    },
    light: {
      color: "#633800",
      backgroundColor: "#fbeedc",
      // borderColor: "#F9E1CB",
      borderColor: `color-mix(in srgb, ${CHART_COLORS[1]} 35%, white)`,
    },
  },
  YELLOW: {
    // solidColor: "#fbe771",
    solidColor: CHART_COLORS[2],
    dark: {
      color: "#ddc672",
      backgroundColor: "#2d2606",
      // borderColor: "#3a3a0f",
      borderColor: `color-mix(in srgb, ${CHART_COLORS[2]} 45%, #1b1d21)`,
    },
    light: {
      color: "#514000",
      backgroundColor: "#f6f0db",
      // borderColor: "#FFEBAD",
      borderColor: `color-mix(in srgb, ${CHART_COLORS[2]} 35%, white)`,
    },
  },
  LIME: {
    // solidColor: "#affd39",
    solidColor: CHART_COLORS[8],
    dark: {
      color: "#b3cdc0",
      backgroundColor: "#1d2826",
      // borderColor: "#2e3910",
      borderColor: `color-mix(in srgb, ${CHART_COLORS[8]} 45%, #1b1d21)`,
    },
    light: {
      color: "#284839",
      backgroundColor: "#ebf2ee",
      // borderColor: "#e2f090",
      borderColor: `color-mix(in srgb, ${CHART_COLORS[8]} 35%, white)`,
    },
  },
  GREEN: {
    solidColor: "#0ed300",
    dark: {
      color: "#7eda99",
      backgroundColor: "#212824",
      borderColor: "#254d3290",
    },
    light: {
      color: "#004d26",
      backgroundColor: "#dbf7e2",
      borderColor: "#C7F4D3",
    },
  },
  AQUA: {
    // solidColor: "#71f5f5",
    solidColor: CHART_COLORS[6],
    dark: {
      color: "#bdc9d0",
      backgroundColor: "#21272c",
      // borderColor: "#254D4D90",
      borderColor: `color-mix(in srgb, ${CHART_COLORS[6]} 45%, #1b1d21)`,
    },
    light: {
      color: "#36444d",
      backgroundColor: "#edf1f2",
      // borderColor: "#c9f0f5",
      borderColor: `color-mix(in srgb, ${CHART_COLORS[6]} 35%, white)`,
    },
  },
  BLUE: {
    // solidColor: "#0d41ce",
    solidColor: CHART_COLORS[0],
    dark: {
      color: "#bfc2ff",
      backgroundColor: "#20214d",
      // borderColor: "#1d3a5a",
      borderColor: `color-mix(in srgb, ${CHART_COLORS[0]} 45%, #1b1d21)`,
    },
    light: {
      color: "#282eae",
      backgroundColor: "#efefff",
      // borderColor: "#D6E5FF",
      borderColor: `color-mix(in srgb, ${CHART_COLORS[0]} 35%, white)`,
    },
  },
  PURPLE: {
    // solidColor: "#9966ff",
    solidColor: CHART_COLORS[5],
    dark: {
      color: "#adc7ff",
      backgroundColor: "#1a263f",
      // borderColor: "#3d2758",
      borderColor: `color-mix(in srgb, ${CHART_COLORS[5]} 45%, #1b1d21)`,
    },
    light: {
      color: "#1c3e87",
      backgroundColor: "#eaf0ff",
      // borderColor: "#E8DDFE",
      borderColor: `color-mix(in srgb, ${CHART_COLORS[5]} 35%, white)`,
    },
  },
  VIOLET: {
    // solidColor: "#e671e6",
    solidColor: CHART_COLORS[7],
    dark: {
      color: "#bfcac4",
      backgroundColor: "#212728",
      // borderColor: "#421f42",
      borderColor: `color-mix(in srgb, ${CHART_COLORS[7]} 45%, #1b1d21)`,
    },
    light: {
      color: "#38453e",
      backgroundColor: "#eef1ef",
      // borderColor: "#F2D6F5",
      borderColor: `color-mix(in srgb, ${CHART_COLORS[7]} 35%, white)`,
    },
  },
  PINK: {
    // solidColor: "#ff91b7",
    solidColor: CHART_COLORS[4],
    dark: {
      color: "#d9c4a9",
      backgroundColor: "#2c251e",
      // borderColor: "#401f2d",
      borderColor: `color-mix(in srgb, ${CHART_COLORS[4]} 45%, #1b1d21)`,
    },
    light: {
      color: "#563d1b",
      backgroundColor: "#f5efe8",
      // borderColor: "#FDDDE7",
      borderColor: `color-mix(in srgb, ${CHART_COLORS[4]} 35%, white)`,
    },
  },
};

/**
 * The colour a category, a tag or an invoice's template is picked from, as
 * ``EnumColors`` draws it.
 */
export const TagColors: Record<
  TagColorEnum,
  (typeof EnumColors)[keyof typeof EnumColors]
> = {
  [TagColorEnum.Aqua]: EnumColors.AQUA,
  [TagColorEnum.Blue]: EnumColors.BLUE,
  [TagColorEnum.Green]: EnumColors.GREEN,
  [TagColorEnum.Grey]: EnumColors.GREY_1,
  [TagColorEnum.Lime]: EnumColors.LIME,
  [TagColorEnum.Orange]: EnumColors.ORANGE,
  [TagColorEnum.Pink]: EnumColors.PINK,
  [TagColorEnum.Purple]: EnumColors.PURPLE,
  [TagColorEnum.Red]: EnumColors.RED,
  [TagColorEnum.Violet]: EnumColors.VIOLET,
  [TagColorEnum.Yellow]: EnumColors.YELLOW,
};

export const ZIndex = {
  // ── Data Grid (Table & Tree) ──
  // Contained within panel stacking contexts (panels use isolation: isolate).
  // Values here are relative to the panel, not the root document.
  GRID: {
    // Row content
    ROW: 1,
    HEADER_SCROLLABLE_CONTAINER: 2,
    ROW_LOCKED_CELLS: 3,
    HEADER_CELL: 5,

    // Tree sticky rows
    STICKY_CONTAINER: 100,
    TREE_ROW_SCROLLABLE: 400,
    TREE_ROW_LOCKED: 500,
    TREE_STICKY_SCROLLABLE: 700,
    TREE_STICKY_LOCKED: 800,

    // Headers
    HEADER_SCROLLABLE: 900,
    HEADER_LOCKED: 1000,
    HEADER_FIXED_ELEMENT: 1001,
    HEADER_CHECKBOX: 1200,
    COLUMN_RESIZE: 1202,
    COLUMN_RESIZE_HOVER: 1203,
    HEADER_UNLOCKED: 2000,

    // Table row stacking (base − rowIndex)
    TABLE_ROW: 10000,
    TABLE_DIALOG: 10000,
  },

  // ── App Layout (root stacking context) ──
  // These compete globally; panels isolate grid internals above.
  APP: {
    PANEL_RESIZE_HANDLE: 10,
    // The layout's footer under the side panels: over the panels and the handle
    // between them, under anything that floats.
    FOOTER: 20,
    // A panel over the content on a phone (``AppLayoutOverlayPanel``): over the
    // view and its footer, under anything that floats.
    OVERLAY_PANEL: 30,
    SELECTION_BAR: 1100,
    POPPER: 1200,
    DIALOG: 1300,
    CONTEXT_MENU: 10001,
  },
};

export { Values };
